import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Trophy, Lock, Check, Flame, Dumbbell, Target, Droplet, Brain, Star, Sparkles, Award, Zap } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import Paywall from "@/components/Paywall";

const CATEGORY_INFO = {
  streak: { name: "Streak", icon: Flame, color: "text-orange-500", bg: "from-orange-500/10" },
  workout: { name: "Træning", icon: Dumbbell, color: "text-blue-500", bg: "from-blue-500/10" },
  tracking: { name: "Tracking", icon: Target, color: "text-green-500", bg: "from-green-500/10" },
  water: { name: "Hydrering", icon: Droplet, color: "text-cyan-500", bg: "from-cyan-500/10" },
  ai: { name: "AI Coach", icon: Brain, color: "text-purple-500", bg: "from-purple-500/10" },
  goals: { name: "Mål", icon: Star, color: "text-yellow-500", bg: "from-yellow-500/10" },
};

export default function AchievementsPage() {
  const { authHeaders } = useAuth();
  const [achievements, setAchievements] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [achievementsRes, statsRes] = await Promise.all([
          axios.get(`${API}/gamification/achievements`, { headers: authHeaders() }),
          axios.get(`${API}/gamification/stats`, { headers: authHeaders() })
        ]);
        // API returns { achievements: [...] }, so extract the array
        const achievementsData = achievementsRes.data.achievements || achievementsRes.data;
        setAchievements(Array.isArray(achievementsData) ? achievementsData : []);
        setStats(statsRes.data);
      } catch (err) {
        console.error("Error fetching achievements:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const categories = ["all", ...Object.keys(CATEGORY_INFO)];
  const filteredAchievements = filter === "all" 
    ? achievements 
    : achievements.filter(a => a.category === filter);

  const earnedCount = achievements.filter(a => a.earned).length;
  const totalPoints = achievements.filter(a => a.earned).reduce((sum, a) => sum + a.points, 0);
  const completionPercent = achievements.length > 0 ? Math.round((earnedCount / achievements.length) * 100) : 0;

  if (loading) {
    return (
      <Paywall>
        <div className="min-h-screen bg-[#09090b]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {[1,2,3,4,5,6,7,8,9,10].map(i => (
                <div key={i} className="bg-[#18181b] border border-white/5 rounded-xl aspect-square animate-pulse" />
              ))}
            </div>
          </div>
        </div>
      </Paywall>
    );
  }

  return (
    <Paywall>
      <div className="min-h-screen bg-[#09090b]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8" data-testid="achievements-page">
          
          {/* Header with Stats */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            
            {/* Main Header Card */}
            <div className="lg:col-span-2 bg-gradient-to-br from-[#18181b] to-[#0f0f12] border border-white/5 rounded-xl p-6 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-60 h-60 bg-[#DFFF00]/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
              <div className="relative">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 rounded-xl bg-[#DFFF00]/10 flex items-center justify-center">
                    <Trophy className="h-8 w-8 text-[#DFFF00]" />
                  </div>
                  <div>
                    <h1 style={{ fontFamily: 'Oswald, sans-serif' }} className="font-bold text-2xl sm:text-3xl uppercase tracking-tight text-white">
                      Achievements
                    </h1>
                    <p className="text-sm text-zinc-500">Lås op for badges og optjen XP</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-zinc-900/50 rounded-xl p-4 text-center">
                    <p style={{ fontFamily: 'JetBrains Mono' }} className="text-2xl sm:text-3xl font-bold text-white" data-testid="earned-count">
                      {earnedCount}
                    </p>
                    <p className="text-xs text-zinc-500 mt-1">af {achievements.length} låst op</p>
                  </div>
                  <div className="bg-zinc-900/50 rounded-xl p-4 text-center">
                    <p style={{ fontFamily: 'JetBrains Mono' }} className="text-2xl sm:text-3xl font-bold text-[#DFFF00]" data-testid="total-xp">
                      {totalPoints.toLocaleString()}
                    </p>
                    <p className="text-xs text-zinc-500 mt-1">XP optjent</p>
                  </div>
                  <div className="bg-zinc-900/50 rounded-xl p-4 text-center">
                    <p style={{ fontFamily: 'JetBrains Mono' }} className="text-2xl sm:text-3xl font-bold text-white" data-testid="completion-percent">
                      {completionPercent}%
                    </p>
                    <p className="text-xs text-zinc-500 mt-1">Fuldført</p>
                  </div>
                </div>
                
                <div className="mt-6">
                  <div className="flex justify-between text-xs text-zinc-500 mb-2">
                    <span>Fremskridt</span>
                    <span>{earnedCount}/{achievements.length}</span>
                  </div>
                  <Progress value={completionPercent} className="h-3 bg-zinc-800" />
                </div>
              </div>
            </div>

            {/* Level Card */}
            <div className="bg-gradient-to-br from-purple-500/10 via-[#18181b] to-[#18181b] border border-white/5 rounded-xl p-6 flex flex-col justify-center">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center">
                  <Zap className="h-6 w-6 text-purple-400" />
                </div>
                <div>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider">Dit Level</p>
                  <p className="text-lg font-bold text-white">{stats?.level?.level_name || "Begynder"}</p>
                </div>
              </div>
              <div className="text-center py-4">
                <p className="text-5xl mb-2">{stats?.level?.level_icon || "🌱"}</p>
                <p style={{ fontFamily: 'JetBrains Mono' }} className="text-xl font-bold text-purple-400">{stats?.total_xp || 0} XP</p>
              </div>
              {stats?.level?.next_level_xp && (
                <div className="mt-4">
                  <Progress value={stats?.level?.progress_percent || 0} className="h-2 bg-zinc-800" />
                  <p className="text-xs text-zinc-500 mt-2 text-center">
                    {stats.level.next_level_xp - (stats?.total_xp || 0)} XP til {stats.level.next_level_name}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap gap-2 mb-6 overflow-x-auto pb-2">
            {categories.map(cat => {
              const info = CATEGORY_INFO[cat];
              const Icon = info?.icon || Trophy;
              const isActive = filter === cat;
              const catCount = cat === "all" 
                ? achievements.length 
                : achievements.filter(a => a.category === cat).length;
              
              return (
                <button
                  key={cat}
                  onClick={() => setFilter(cat)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all whitespace-nowrap ${
                    isActive 
                      ? 'bg-[#DFFF00] text-black' 
                      : 'bg-[#18181b] border border-white/5 text-zinc-400 hover:border-white/10 hover:text-white'
                  }`}
                  data-testid={`filter-${cat}`}
                >
                  <Icon className="h-4 w-4" />
                  {cat === "all" ? "Alle" : info?.name}
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${isActive ? 'bg-black/20' : 'bg-zinc-800'}`}>
                    {catCount}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Achievements Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {filteredAchievements.map((achievement, idx) => {
              const catInfo = CATEGORY_INFO[achievement.category];
              
              return (
                <div
                  key={achievement.id}
                  className={`group relative bg-[#18181b] border rounded-xl overflow-hidden transition-all hover:scale-[1.02] ${
                    achievement.earned 
                      ? 'border-[#DFFF00]/20 hover:border-[#DFFF00]/40 shadow-[0_0_20px_-5px_rgba(223,255,0,0.1)]' 
                      : 'border-white/5 hover:border-white/10 opacity-60 grayscale hover:grayscale-0 hover:opacity-80'
                  }`}
                  data-testid={`achievement-${achievement.id}`}
                >
                  {/* Glow effect for earned */}
                  {achievement.earned && (
                    <div className="absolute inset-0 bg-gradient-to-br from-[#DFFF00]/5 to-transparent pointer-events-none" />
                  )}
                  
                  <div className="relative p-4">
                    {/* Icon */}
                    <div className={`w-16 h-16 mx-auto mb-3 rounded-xl flex items-center justify-center text-4xl ${
                      achievement.earned 
                        ? `bg-gradient-to-br ${catInfo?.bg || 'from-[#DFFF00]/10'} to-transparent` 
                        : 'bg-zinc-800/50'
                    }`}>
                      {achievement.earned ? achievement.icon : <Lock className="h-7 w-7 text-zinc-600" />}
                    </div>
                    
                    {/* Name */}
                    <h3 className="font-bold text-white text-center text-sm mb-1 line-clamp-1">{achievement.name}</h3>
                    
                    {/* Description */}
                    <p className="text-xs text-zinc-500 text-center line-clamp-2 min-h-[32px]">{achievement.description}</p>
                    
                    {/* XP Badge */}
                    <div className="flex items-center justify-center gap-2 mt-3">
                      <span className={`text-xs px-2 py-1 rounded-full ${achievement.earned ? 'bg-[#DFFF00]/10 text-[#DFFF00]' : 'bg-zinc-800 text-zinc-500'}`}>
                        +{achievement.points} XP
                      </span>
                    </div>
                    
                    {/* Earned Badge */}
                    {achievement.earned && (
                      <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center">
                        <Check className="h-3.5 w-3.5 text-emerald-400" />
                      </div>
                    )}
                    
                    {/* Category Tag */}
                    <div className="absolute top-2 left-2">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full bg-zinc-900/80 ${catInfo?.color || 'text-zinc-400'} uppercase tracking-wider`}>
                        {catInfo?.name || achievement.category}
                      </span>
                    </div>
                  </div>
                  
                  {/* Earned Date */}
                  {achievement.earned && achievement.earned_at && (
                    <div className="px-4 py-2 bg-zinc-900/50 border-t border-white/5">
                      <p className="text-[10px] text-zinc-500 text-center">
                        Låst op {new Date(achievement.earned_at).toLocaleDateString('da-DK', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {filteredAchievements.length === 0 && (
            <div className="text-center py-16">
              <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-zinc-800/50 flex items-center justify-center">
                <Trophy className="h-10 w-10 text-zinc-600" />
              </div>
              <p className="text-zinc-500 text-lg">Ingen achievements i denne kategori</p>
              <p className="text-zinc-600 text-sm mt-1">Prøv en anden kategori</p>
            </div>
          )}

          {/* Motivation Footer */}
          {earnedCount < achievements.length && (
            <div className="mt-8 bg-gradient-to-r from-[#18181b] via-[#1a1a1f] to-[#18181b] border border-white/5 rounded-xl p-6 text-center">
              <div className="flex items-center justify-center gap-3 mb-2">
                <Sparkles className="h-5 w-5 text-[#DFFF00]" />
                <span className="text-white font-medium">Fortsæt det gode arbejde!</span>
              </div>
              <p className="text-zinc-500 text-sm">
                Du mangler kun <span className="text-[#DFFF00] font-bold">{achievements.length - earnedCount}</span> achievements for at fuldføre samlingen
              </p>
            </div>
          )}
        </div>
      </div>
    </Paywall>
  );
}
